import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.tsx';
import api from '../services/api.ts';
import { formatCurrency } from '../utils/currency.ts';
import { formatStatus, getStatusColor } from '../utils/statusFormatter.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  TruckIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

// Types
interface Vehicle {
  id: string;
  make: string;
  model: string;
  year: number;
  licensePlate: string;
  color: string;
  purchasePrice: number;
  sellingPrice?: number;
  status: 'in_stock' | 'sold' | 'reserved' | 'exchanged';
  purchaseDate: string;
  soldDate?: string;
  assetId: string;
}

const Vehicles: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [sortField, setSortField] = useState<keyof Vehicle>('purchaseDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'in_stock' | 'sold' | 'reserved' | 'exchanged'>(
    'in_stock'
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    data: vehicles,
    isLoading,
    error,
    refetch,
  } = useQuery<Vehicle[]>(
    'vehicles',
    async () => {
      const response = await api.get('/vehicles');
      return response.data;
    },
    {
      staleTime: 1000 * 60 * 5, // 5 minutes
    }
  );

  // Remover handleSort não utilizado
  // const handleSort = (field: keyof Vehicle) => {
  //   if (field === sortField) {
  //     setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
  //   } else {
  //     setSortField(field);
  //     setSortDirection('asc');
  //   }
  // };

  const filteredVehicles =
    vehicles?.filter(vehicle => {
      // Filtro de busca por texto
      const matchesSearch =
        vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.licensePlate.toLowerCase().includes(searchTerm.toLowerCase());

      // Filtro de status
      const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter;

      return matchesSearch && matchesStatus;
    }) || [];

  const sortedVehicles = [...filteredVehicles].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (aValue !== undefined && bValue !== undefined) {
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const handleDelete = (id: string) => {
    setVehicleToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!vehicleToDelete) return;

    setIsDeleting(true);
    try {
      await api.delete(`/vehicles/${vehicleToDelete}`);
      refetch();
      setIsDeleteModalOpen(false);
      setVehicleToDelete(null);
    } catch (error) {
      console.error('Erro ao excluir veículo:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setVehicleToDelete(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-red-500 p-8">
        <p className="text-lg">Erro ao carregar veículos.</p>
        <p className="text-sm mt-2">Por favor, tente novamente.</p>
      </div>
    );
  }

  // Calcular valores baseados nos veículos filtrados
  const activeVehicles = filteredVehicles?.filter(v => v.status === 'in_stock').length || 0;
  const totalPurchaseValue =
    filteredVehicles?.reduce((sum, vehicle) => {
      const price = Number(vehicle.purchasePrice) || 0;
      return sum + price;
    }, 0) || 0;

  const totalSellingValue =
    filteredVehicles?.reduce((sum, vehicle) => {
      const price = Number(vehicle.sellingPrice) || 0;
      return sum + price;
    }, 0) || 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <TruckIcon className="h-8 w-8 mr-3 text-primary-600" />
            Veículos
          </h1>
          <p className="text-gray-600">Gerencie sua frota de veículos</p>
        </div>
        <button
          onClick={() => navigate('/vehicles/new')}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-medium transition-colors flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Novo Veículo
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <TruckIcon className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Veículos</p>
                <p className="text-2xl font-bold text-gray-900">{filteredVehicles?.length || 0}</p>
              </div>
            </div>
            <div className="flex items-center">
              <CalendarIcon className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Em Estoque</p>
                <p className="text-2xl font-bold text-gray-900">{activeVehicles}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total de Compra</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(totalPurchaseValue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Valor Total de Venda</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(totalSellingValue)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por marca, modelo ou placa..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-[200px]">
            <select
              value={statusFilter}
              onChange={e =>
                setStatusFilter(e.target.value as 'all' | 'in_stock' | 'sold' | 'reserved' | 'exchanged')
              }
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            >
              <option value="in_stock">Em Estoque</option>
              <option value="exchanged">Permutados</option>
              <option value="sold">Vendidos</option>
              <option value="all">Todos os Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Vehicles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sortedVehicles.map(vehicle => (
          <div
            key={vehicle.id}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow"
          >
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {vehicle.make} {vehicle.model}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {vehicle.year} • {vehicle.color}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(vehicle.status)}`}
                >
                  {formatStatus(vehicle.status)}
                </span>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Placa:</span>
                  <span className="font-semibold text-gray-900">{vehicle.licensePlate}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Valor de Compra:</span>
                  <span className="font-semibold text-gray-900">
                    {formatCurrency(vehicle.purchasePrice)}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Valor de Venda:</span>
                  <span className="font-semibold text-green-600">
                    {vehicle.sellingPrice ? formatCurrency(vehicle.sellingPrice) : '-'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Data de Compra:</span>
                  <span className="font-semibold text-gray-900">
                    {new Date(vehicle.purchaseDate).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                {vehicle.status === 'sold' && vehicle.soldDate && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Data de Venda:</span>
                    <span className="font-semibold text-gray-900">
                      {new Date(vehicle.soldDate).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => navigate(`/vehicles/${vehicle.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-200 text-sm font-medium rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <EyeIcon className="h-4 w-4 mr-1" />
                  Ver
                </button>
                {vehicle.assetId && vehicle.status !== 'sold' && vehicle.status !== 'exchanged' && (
                  <button
                    onClick={() => navigate(`/exchanges/new?originType=vehicle&originId=${vehicle.assetId}`)}
                    className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-purple-200 text-sm font-medium rounded-xl text-purple-700 bg-purple-50 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors duration-200"
                  >
                    Iniciar Permuta
                  </button>
                )}
                <button
                  onClick={() => navigate(`/edit-vehicle/${vehicle.id}`)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-primary-200 text-sm font-medium rounded-xl text-primary-700 bg-primary-50 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors duration-200"
                >
                  <PencilIcon className="h-4 w-4 mr-1" />
                  Editar
                </button>
                {hasPermission('assets.delete') && (
                  <button
                    onClick={() => handleDelete(vehicle.id)}
                    className="px-3 py-2 border border-red-200 text-sm font-medium rounded-xl text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {sortedVehicles.length === 0 && (
        <div className="text-center py-12">
          <TruckIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum veículo encontrado</h3>
          <p className="text-gray-500 mb-6">
            {searchTerm ? 'Tente ajustar sua busca.' : 'Comece adicionando seu primeiro veículo.'}
          </p>
          {!searchTerm && (
            <button
              onClick={() => navigate('/vehicles/new')}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 mx-auto transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Adicionar Veículo
            </button>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Excluir Veículo"
        message="Tem certeza que deseja excluir este veículo? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Vehicles;
